import React, { useState } from 'react';
import { useApi } from '../../lib/apiClient';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Input,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@pagespeedify/ui';
import { toast } from 'sonner';

interface EditImageOptimizationTaskFormProps {
    taskId: string;
    initialTitle: string;
    initialTag?: string;
    initialPriority?: string;
    onClose: () => void;
    onSuccess: () => void;
}

export const EditImageOptimizationTaskForm: React.FC<EditImageOptimizationTaskFormProps> = ({
    taskId,
    initialTitle,
    initialTag,
    initialPriority,
    onClose,
    onSuccess,
}) => {
    const api = useApi();
    const [title, setTitle] = useState(initialTitle);
    const [tag, setTag] = useState(initialTag || '');
    const [priority, setPriority] = useState(initialPriority || 'medium');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim()) return;

        setIsSubmitting(true);
        try {
            await api.patch(`/tasks/${taskId}`, {
                title: title.trim(),
                tag: tag.trim() || null,
                priority,
            });
            toast.success('Task updated successfully');
            onSuccess();
        } catch (error) {
            console.error('Failed to update task', error);
            toast.error('Failed to update task details');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle>Edit Optimization Task</CardTitle>
                </div>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                        <Label htmlFor="title">Title</Label>
                        <Input
                            id="title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Task Title"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="tag">Tag</Label>
                        <Input
                            id="tag"
                            value={tag}
                            onChange={(e) => setTag(e.target.value)}
                            placeholder="Add a tag..."
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="priority">Task Priority</Label>
                        <Select value={priority} onValueChange={setPriority}>
                            <SelectTrigger id="priority" className="w-full mt-1">
                                <SelectValue placeholder="Select priority" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
};
